/**
 * Which languages the app offers is decided by the backend, not the frontend.
 *
 * The published catalog manifest lists every locale the CMS has enabled, with its display names
 * and text direction. `config.ts` fetches that manifest and pushes the result in here; the language
 * switcher reads it. Enabling a language in the CMS and publishing therefore makes it appear to
 * users with no frontend release.
 *
 * This module deliberately has no i18next dependency and no side effects, so components (and their
 * tests) can read the locale list without pulling in i18n initialisation.
 */

export interface CatalogLocale {
  code: string;
  name: string;
  nativeName: string;
  dir: 'ltr' | 'rtl';
}

// Seeded with the bundled language so the switcher still renders before the manifest resolves,
// and keeps working if the CDN is unreachable.
const FALLBACK_LOCALES: CatalogLocale[] = [
  { code: 'en', name: 'English', nativeName: 'English', dir: 'ltr' },
];

let catalogLocales: CatalogLocale[] = FALLBACK_LOCALES;
const listeners = new Set<() => void>();

/** Current snapshot. Reference is stable between manifest updates, as useSyncExternalStore needs. */
export const getCatalogLocales = (): CatalogLocale[] => catalogLocales;

export const subscribeCatalogLocales = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const setCatalogLocales = (locales: CatalogLocale[]): void => {
  if (locales.length === 0) return; // never blank the switcher on a malformed manifest
  catalogLocales = locales;
  for (const listener of listeners) listener();
};

/** The backend-declared direction for a locale, if it publishes one. */
export const getLocaleDir = (code: string): 'ltr' | 'rtl' | undefined =>
  catalogLocales.find(l => l.code === code)?.dir;
